/**
 * Hand-rolled `POST {baseUrl}{refreshPath}` call.
 *
 * The path is `/api/v1/auth/refresh` for tenant audiences (admin / mobile)
 * and `/api/v1/_platform/auth/refresh` for the platform audience (CC-53 /
 * be-047). Both endpoints return identical body shapes
 * (`{accessToken, refreshToken, tokenType, expiresIn}`); the audience is
 * selected once in `configureAuthNetwork` during app bootstrap and read here
 * via `getRefreshPath()` so this module stays React-free and arg-injected.
 *
 * Rationale for NOT using `@linkhr/api-client`'s `createApiClient` here:
 *   The api-client's 401-retry middleware (packages/api-client/src/middleware/refresh.ts)
 *   catches 401s and calls the TokenProvider's `tryRefresh` — which ultimately
 *   calls *this* function. Routing the refresh call back through the middleware
 *   would create an infinite loop when the refresh itself returns 401
 *   (REFRESH_EXPIRED or REFRESH_FAMILY_REVOKED).
 *
 *   Auth endpoints are the ONE boundary where direct `fetch()` in non-app code
 *   is allowed per project conventions commentary.
 *
 * Behavior contract:
 *   - The credential is the HttpOnly refresh cookie and nothing else. The
 *     request has no body; `credentials: "include"` sends the cookie and
 *     `X-Refresh-Audience` names which per-audience cookie to read. Nothing is
 *     read from or written to client storage (see refreshTokenStore for why a
 *     stored copy got people signed out).
 *   - On 200: the response's Set-Cookie has already rotated the cookie. Parses
 *     `{accessToken, expiresIn}` and returns `{accessToken, exp, issuedAt}`.
 *   - On 401: terminal, never retried. Presenting a token that has already
 *     rotated (`AUTH_TOKEN_REUSED`) revokes its whole family and the same
 *     response clears the cookies, so no second request can succeed.
 *   - On 401 + `errorCode: "AUTH_TOKEN_REUSED"` (or the mock's
 *     `"REFRESH_FAMILY_REVOKED"`): broadcasts
 *     `FAMILY_REVOKED` on the BC so every tab's AuthProvider can force-logout
 *     with deep-link preserved. Then throws.
 *   - On 403 + `errorCode: "AUTH_ADMIN_AUDIENCE_NOT_PERMITTED"`: the account
 *     lost its admin-capable role mid-session. Terminal, and the code rides the
 *     thrown error so the app root can land the user on the employee app
 *     instead of a generic "session revoked".
 *   - On a 5xx: throws `REFRESH_SERVER_ERROR` — transient, like no answer at all
 *     (`REFRESH_NETWORK_ERROR`) or a throttle (`REFRESH_RATE_LIMITED`). None of
 *     the three says anything about the session, so none may sign anyone out.
 *   - On any other non-2xx: throws with the backend's errorCode (or a reasonable
 *     fallback) so the caller can pattern-match. The code travels on the error's
 *     `errorCode` property — NOT only inside the message string.
 */
import {
  getAcceptLanguageHeader,
  getAudience,
  getAudienceHeader,
  getAuthBaseUrl,
  getAuthChannel,
  getRefreshPath,
  hasHrReads,
  subscribeToAuthChannel,
} from "./authNetworkConfig.js";
import { fetchEffectivePermissions } from "../bootstrap/effectivePermissions.js";
import { decodeJwtClaims } from "../tokens/jwt.js";
import type { AuthBroadcastMessage } from "../channel/broadcastSchema.js";

export interface RefreshSuccess {
  accessToken: string;
  /** seconds since epoch */
  exp: number;
  /** seconds since epoch */
  issuedAt: number;
  /**
   * Backend-issued effective permission codes, fetched after token rotation
   * succeeds. `undefined` when the perms fetch failed (fail-closed — the
   * caller can preserve any previously hydrated Set rather than wiping
   * gating mid-session). Plan 08.1-02 D-08.2 — perms may change mid-session
   * if HR admin re-grants roles, so refresh is a hydration trigger.
   */
  effectivePermissions?: readonly string[];
  /**
   * `TokenResponse.mustChangePassword`: the account still holds a temporary
   * password, so the new token opens only the password change, refresh and
   * logout. The backend re-reads the account on every rotation.
   */
  mustChangePassword: boolean;
}

interface RefreshResponseBody {
  accessToken: string;
  /** seconds of lifetime for the new access token */
  expiresIn?: number;
  tokenType?: string;
  mustChangePassword?: boolean;
}

interface RefreshErrorBody {
  errorCode?: string;
  title?: string;
}

/**
 * A refresh failure that carries the wire `errorCode` instead of flattening it
 * into a message string. The api-client's refresh middleware rebuilds an
 * `ApiError` from these fields (duck-typed — it cannot import this package
 * without a cycle), so `onAuthFailure` can tell a demotion (403
 * AUTH_ADMIN_AUDIENCE_NOT_PERMITTED) from an ordinary expiry (401).
 */
interface RefreshFailedError extends Error {
  errorCode: string;
  status: number;
  /** Backend's pre-localized RFC 7807 title, when the body carried one. */
  title?: string;
}

function refreshFailedError(
  errorCode: string,
  status: number,
  title: string | undefined,
): RefreshFailedError {
  return Object.assign(new Error(`Refresh failed: ${errorCode}`), {
    errorCode,
    status,
    ...(title !== undefined ? { title } : {}),
  });
}

/**
 * Distinct error code for a rate-limited refresh (HTTP 429). `openapi.json`
 * documents only a 200 for `/auth/refresh`; a gateway 429 is undocumented and
 * usually carries an opaque body, so the generic failure path below would
 * mis-read it as `REFRESH_EXPIRED` and wipe the still-valid token → hard
 * logout. This code lets the api-client refresh middleware recognise the 429 as
 * TRANSIENT and keep the session alive. Mirrors `login.ts`'s `AUTH_RATE_LIMITED`.
 */
export const REFRESH_RATE_LIMITED = "REFRESH_RATE_LIMITED";

/**
 * Distinct error code for a refresh request that got no answer: `fetch`
 * rejected (offline, DNS, a dropped connection). TRANSIENT like a 429. No
 * response means no verdict on the session, so a network blip must not sign
 * the user out; the next 401 or the proactive timer tries again once the
 * network is back.
 */
export const REFRESH_NETWORK_ERROR = "REFRESH_NETWORK_ERROR";

/**
 * Distinct error code for a refresh the server could not answer: any 5xx,
 * whether from the backend itself or the gateway in front of it while the
 * backend restarts. TRANSIENT like a network error. The generic path below
 * used to label an opaque 502/503 `REFRESH_EXPIRED`, and the api-client then
 * signed the user out: a deploy signed out everyone whose token came due
 * during it. Only an answer that refuses the session (401/403) may do that.
 */
export const REFRESH_SERVER_ERROR = "REFRESH_SERVER_ERROR";

/** Shape of the typed error thrown when a refresh is rate-limited. */
export interface RefreshRateLimitedError extends Error {
  errorCode: typeof REFRESH_RATE_LIMITED;
  isRefreshRateLimited: true;
  /** Seconds from the `Retry-After` header when the gateway supplied one. */
  retryAfterSeconds?: number;
  /**
   * True when the throw came from the shared-cooldown ENTRY GATE without any
   * network call. The boot retry loop uses this to avoid counting a fast-fail
   * as its single retry attempt (a tab that extended the cooldown mid-sleep
   * would otherwise burn the retry and bounce a valid session to /login).
   */
  isCooldownFastFail?: true;
}

/** Bounded retry budget for a rate-limited refresh before we give up (and keep
 * the session alive without logging out). Kept at ONE because the retry runs
 * while the cross-tab Web Lock is held — every queued tab would otherwise burn
 * its own full budget serially, prolonging bucket exhaustion. */
const REFRESH_429_MAX_RETRIES = 1;
/** Upper bound (ms) on any single backoff wait so a huge `Retry-After` can't
 * hang the app on a throttled refresh. */
const REFRESH_429_BACKOFF_CAP_MS = 2000;

/**
 * Cross-tab refresh cooldown. When any tab observes a 429 it publishes a
 * "don't bother until" timestamp; every other refresh path checks it BEFORE
 * hitting the network and fails fast with `REFRESH_RATE_LIMITED` instead of
 * queuing behind the Web Lock only to burn its own retry against an exhausted
 * bucket. localStorage so it's shared across tabs without needing a BC
 * round-trip at call time.
 */
export const REFRESH_COOLDOWN_KEY = "linkhr:refresh-cooldown-until";
const REFRESH_COOLDOWN_DEFAULT_MS = 3000;
const REFRESH_COOLDOWN_MAX_MS = 60_000;

/** Guarded `window.localStorage` access (private browsing / disabled storage /
 * some test harnesses throw). */
function tryLocalStorage<T>(op: (ls: Storage) => T, fallback: T): T {
  try {
    if (typeof window === "undefined") return fallback;
    const ls = window.localStorage;
    if (!ls) return fallback;
    return op(ls);
  } catch {
    return fallback;
  }
}

// Tab-local mirror of the cooldown — keeps the fast-fail working when storage
// is disabled.
let cooldownMemoryUntil = 0;

function readCooldownUntil(): number {
  const raw = tryLocalStorage((ls) => ls.getItem(REFRESH_COOLDOWN_KEY), null);
  if (raw !== null) {
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(n, cooldownMemoryUntil);
  }
  return cooldownMemoryUntil;
}

/**
 * Current shared cooldown expiry (ms epoch; 0 = none). Exported for the boot
 * path, which must wait out the ACTUAL cooldown before its single retry — a
 * shorter wait would just fast-fail at this module's entry gate.
 */
export function getRefreshCooldownUntil(): number {
  return readCooldownUntil();
}

/**
 * BC subscriber hook (see `setupAuthChannelHandlers` + the bootstrap mirror
 * below): mirror another tab's cooldown into this tab's memory. Second
 * transport for the storage-disabled case where localStorage can't carry it.
 */
export function setRefreshCooldownMemory(untilMs: number): void {
  // `0` lifts the cooldown — recovery signal wins. Accepted residual: a stale
  // delayed zero could briefly lift an active cooldown; the next 429 republishes.
  if (untilMs === 0) {
    cooldownMemoryUntil = 0;
    return;
  }
  // Monotonic: a delayed/older broadcast must never SHORTEN an active cooldown.
  if (untilMs > cooldownMemoryUntil) {
    cooldownMemoryUntil = untilMs;
  }
}

// The BC mirror must be listening DURING bootstrap — `setupAuthChannelHandlers`
// only installs when React's AuthProvider mounts (after `refreshOnLoad`), so a
// cold-booting tab in a storage-disabled environment would miss cooldown
// broadcasts posted while it sleeps. Installed lazily from `performRefresh` /
// `refreshOnLoad` (both run after `configureAuthNetwork`).
let cooldownMirrorInstalled = false;

export function ensureCooldownMirrorInstalled(): void {
  if (cooldownMirrorInstalled) return;
  try {
    subscribeToAuthChannel((msg) => {
      if (msg.type === "REFRESH_COOLDOWN") setRefreshCooldownMemory(msg.untilMs);
    });
    cooldownMirrorInstalled = true;
  } catch {
    // Channel unavailable (no BroadcastChannel support) — storage/memory
    // transports still apply; retried on the next call.
  }
}

/** Post the cooldown on the auth BC — BroadcastChannel doesn't echo to the
 * sender, so this only updates OTHER tabs (their memory mirror). */
function broadcastCooldown(untilMs: number): void {
  try {
    getAuthChannel().postMessage({
      type: "REFRESH_COOLDOWN",
      untilMs,
    } satisfies AuthBroadcastMessage);
  } catch {
    // Channel unavailable — localStorage/memory transports still apply.
  }
}

function publishCooldown(retryAfterSeconds: number | undefined): void {
  const ms = Math.min(
    retryAfterSeconds !== undefined ? retryAfterSeconds * 1000 : REFRESH_COOLDOWN_DEFAULT_MS,
    REFRESH_COOLDOWN_MAX_MS,
  );
  // Monotonic like the receive path: a fresh short cooldown (e.g. the 3s
  // default from an opaque 429) must never SHORTEN a longer one already in
  // effect — e.g. a 60s Retry-After mirrored in from another tab on a
  // storage-disabled browser. The winning value is what gets stored AND
  // broadcast. Only clearRefreshCooldown()'s explicit zero lifts a cooldown.
  const until = Math.max(Date.now() + ms, readCooldownUntil());
  cooldownMemoryUntil = until;
  tryLocalStorage((ls) => ls.setItem(REFRESH_COOLDOWN_KEY, String(until)), undefined);
  broadcastCooldown(until);
}

/**
 * Lift the shared refresh cooldown. Called internally when a throttled bucket
 * recovers; exported so login/logout flows and tests can reset throttle state
 * (a fresh credential set makes a stale cooldown meaningless).
 */
export function clearRefreshCooldown(): void {
  cooldownMemoryUntil = 0;
  tryLocalStorage((ls) => ls.removeItem(REFRESH_COOLDOWN_KEY), undefined);
  broadcastCooldown(0);
}

/** Build the typed rate-limited error (entry fast-fail + post-retry throw). */
function refreshRateLimitedError(
  retryAfterSeconds: number | undefined,
  opts?: { fastFail?: boolean },
): RefreshRateLimitedError {
  return Object.assign(new Error("Refresh failed: REFRESH_RATE_LIMITED"), {
    // Cast pins the literal — a fresh object-literal property would otherwise
    // widen it to `string` and break the typed-error contract.
    errorCode: REFRESH_RATE_LIMITED as typeof REFRESH_RATE_LIMITED,
    isRefreshRateLimited: true as const,
    ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
    ...(opts?.fastFail === true ? { isCooldownFastFail: true as const } : {}),
  });
}

/**
 * Parse a `Retry-After` header — RFC 9110 allows BOTH delta-seconds and an
 * HTTP-date. Delta form parses directly; date form converts to seconds from
 * now (floored at 0). Garbage → undefined so callers use their default backoff.
 */
export function parseRetryAfterSeconds(res: Response): number | undefined {
  const raw = res.headers.get("retry-after");
  if (raw === null) return undefined;
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (/^\d+$/.test(trimmed)) return Number.parseInt(trimmed, 10);
  const dateMs = Date.parse(trimmed);
  if (!Number.isNaN(dateMs)) return Math.max(0, Math.ceil((dateMs - Date.now()) / 1000));
  return undefined;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * One cookie-only `POST {baseUrl}{refreshPath}` attempt: no body, so the
 * backend resolves the session from the HttpOnly refresh cookie that
 * `credentials:"include"` sends.
 */
async function postRefresh(baseUrl: string): Promise<Response> {
  // X-Refresh-Audience (CC-277) tells the backend which per-audience refresh
  // cookie to read on this shared-host path.
  try {
    return await fetch(baseUrl + getRefreshPath(), {
      method: "POST",
      credentials: "include",
      headers: { ...getAudienceHeader(), ...getAcceptLanguageHeader() },
    });
  } catch (cause) {
    // Only the request itself failing lands here; every HTTP answer, 401
    // included, is classified by the caller.
    throw Object.assign(new Error(`Refresh failed: ${REFRESH_NETWORK_ERROR}`, { cause }), {
      errorCode: REFRESH_NETWORK_ERROR,
      status: 0,
    });
  }
}

export async function performRefresh(): Promise<RefreshSuccess> {
  ensureCooldownMirrorInstalled();
  // The audience this call speaks for. Read once: it can be re-pointed while
  // the request is out (the global login probes `admin`, then `mobile`), and
  // an answer about one audience must never be announced to the other's tabs.
  const startedAud = getAudience();

  // Shared cooldown gate: some tab already saw a 429 and the bucket hasn't
  // recovered — fail fast (typed, transient, token untouched) without a
  // network call or a pointless in-lock retry. Flagged as a fast-fail so the
  // boot retry loop doesn't count it as a real attempt.
  const cooldownUntil = readCooldownUntil();
  if (Date.now() < cooldownUntil) {
    throw refreshRateLimitedError(Math.ceil((cooldownUntil - Date.now()) / 1000), {
      fastFail: true,
    });
  }

  const baseUrl = getAuthBaseUrl();

  // Every audience's credential is its HttpOnly cookie, so the request is
  // always cookie-only. A 401 is final: there is no second credential to try.
  let res = await postRefresh(baseUrl);
  let errorBody: RefreshErrorBody | null = res.ok
    ? null
    : ((await res.json().catch(() => ({}))) as RefreshErrorBody);

  // 429 rate-limited — TRANSIENT, not an expired session (treating it as one
  // caused the "429 → hard logout" regression): publish the shared cooldown
  // IMMEDIATELY (queued tabs behind the Web Lock fail fast at their own entry
  // gate instead of serially burning retries), then back off once with jitter and retry within a small bounded cap. If
  // it's still throttled, throw a DISTINCT typed error the api-client
  // middleware treats as transient (no onAuthFailure / no logout).
  if (res.status === 429) {
    publishCooldown(parseRetryAfterSeconds(res));
    for (let attempt = 1; attempt <= REFRESH_429_MAX_RETRIES && res.status === 429; attempt++) {
      const retryAfterSeconds = parseRetryAfterSeconds(res);
      const baseMs =
        retryAfterSeconds !== undefined
          ? Math.min(retryAfterSeconds * 1000, REFRESH_429_BACKOFF_CAP_MS)
          : Math.min(250 * attempt, REFRESH_429_BACKOFF_CAP_MS);
      // Jitter de-syncs a burst of tabs/timers that all hit the shared refresh
      // bucket at once (the exact trigger of rapid hard-navigation logout).
      const jitterMs = Math.floor(Math.random() * 250);
      await sleep(baseMs + jitterMs);
      // A 429 consumed nothing, so the cookie is still the current credential.
      res = await postRefresh(baseUrl);
    }
    if (res.status === 429) {
      const retryAfterSeconds = parseRetryAfterSeconds(res);
      publishCooldown(retryAfterSeconds);
      throw refreshRateLimitedError(retryAfterSeconds);
    }
    // The bucket recovered within the budget — lift the shared cooldown so
    // other tabs stop failing fast. 200 → fall through to the success path
    // (errorBody no longer applies); any other non-2xx → re-read its body so
    // the generic classifier below sees the CURRENT response, not the 429.
    clearRefreshCooldown();
    errorBody = res.ok ? null : ((await res.json().catch(() => ({}))) as RefreshErrorBody);
  }

  if (!res.ok) {
    const body = errorBody ?? {};
    // No verdict on the session: the server failed, it did not refuse. Typed
    // as transient, the token and the cookie stay as they are.
    if (res.status >= 500) {
      throw Object.assign(new Error(`Refresh failed: ${REFRESH_SERVER_ERROR}`), {
        errorCode: REFRESH_SERVER_ERROR,
        status: res.status,
      });
    }
    const errorCode = body.errorCode ?? "REFRESH_EXPIRED";
    // The family is gone for every tab: the backend revokes it on reuse and
    // answers `AUTH_TOKEN_REUSED` (the mock answers `REFRESH_FAMILY_REVOKED`).
    // Only tell THIS audience's tabs. The global login re-points the audience
    // mid-probe, and a revoked employee family must not force-logout the HR
    // Admin tabs listening on the other channel.
    if (
      (errorCode === "AUTH_TOKEN_REUSED" || errorCode === "REFRESH_FAMILY_REVOKED") &&
      getAudience() === startedAud
    ) {
      getAuthChannel().postMessage({
        type: "FAMILY_REVOKED",
        reason: "reuse",
        correlationId: res.headers.get("x-correlation-id") ?? "(none)",
      } satisfies AuthBroadcastMessage);
    }
    throw refreshFailedError(errorCode, res.status, body.title);
  }

  const body = (await res.json()) as RefreshResponseBody;

  // Audience-mismatch guard (CC-277 defense-in-depth). With X-Refresh-Audience
  // sent, the backend returns a token for THIS app's audience. If it doesn't
  // (e.g. a legacy-cookie fallback on the shared host resolved the other app's
  // session), a wrong-audience token leaked in — refuse it and force a clean
  // re-login rather than silently operating as the wrong persona and 403ing.
  const refreshedClaims = decodeJwtClaims(body.accessToken);
  if (refreshedClaims !== null && refreshedClaims.aud !== getAudience()) {
    throw new Error("Refresh failed: AUDIENCE_MISMATCH");
  }

  // The rotated refresh token rode this response's Set-Cookie. The body may
  // echo it (the backend always does for `mobile`); it is deliberately NOT
  // kept — a client-side copy is a second carrier that can fall behind the
  // cookie (see refreshTokenStore).
  const nowSec = Math.floor(Date.now() / 1000);
  const exp = nowSec + (body.expiresIn ?? 900);

  // Fetch effective-permissions alongside the rotated token — but only for
  // the HR audiences. An accounting bearer is refused there (its permissions
  // come from /me/permissions). Platform admins authenticate against /_platform/* and
  // have no permission catalog (their access is gated by aud=platform itself
  // + the residual JWT roles claim per the LINK_STAFF carve-out). Calling
  // /users/me/effective-permissions with a platform token would 401 by
  // design and produce console error noise on every refresh.
  //
  // Fail-closed for tenant audiences: fetchEffectivePermissions never throws
  // and returns [] on any non-2xx / network error. Empty Set is a legitimate
  // state for newly-provisioned users.
  const result: RefreshSuccess = {
    accessToken: body.accessToken,
    exp,
    issuedAt: nowSec,
    mustChangePassword: body.mustChangePassword === true,
  };
  // A restricted token would only earn a 403 from the permissions endpoint.
  if (hasHrReads(getAudience()) && !result.mustChangePassword) {
    result.effectivePermissions = await fetchEffectivePermissions({
      apiBaseUrl: baseUrl,
      accessToken: body.accessToken,
    });
  }
  return result;
}
